'use client';

import { useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { CARD_FALLBACK_IMAGE } from '@/lib/utils/thumbnail-url';

interface CourseCardProps {
  title: string;
  duration: string;
  backgroundImage: string;
  href: string;
  type?: string;
}

export function CourseCard({
  title,
  duration,
  backgroundImage,
  href,
  type = 'Course',
}: CourseCardProps) {
  const [imgError, setImgError] = useState(false);
  const effectiveImage =
    imgError || !backgroundImage?.trim()
      ? CARD_FALLBACK_IMAGE
      : backgroundImage;
  const hasImage = typeof effectiveImage === 'string' && effectiveImage.trim().length > 0;

  return (
    <Link href={href} className="flex flex-col w-full group" title={title}>
      {/* Image - Responsive aspect ratio; fallback when missing or fails to load */}
      <div className="relative w-full aspect-[16/9] overflow-hidden bg-surface-3">
        {hasImage ? (
          <Image
            src={effectiveImage}
            alt={`${type} image for ${title}`}
            fill
            className="object-contain group-hover:scale-105 transition-transform duration-300"
            sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 320px"
            unoptimized={effectiveImage.startsWith('data:')}
            onError={() => setImgError(true)}
          />
        ) : null}
      </div>

      {/* Content below image - flex-1 so all cards same height; label/duration at bottom */}
      <div className="p-6 flex flex-col flex-1 min-h-0 bg-surface-2 card-content-hover transition-colors duration-300">
        {/* Title - up to 2 lines */}
        <h4 className="cs-h4 line-clamp-2 min-h-0 break-words">
          {title}
        </h4>

        {/* Label and Duration - mt-auto keeps at bottom when title is 1 line (empty space above is fine) */}
        <div className="flex items-center gap-2 text-text-default-light flex-shrink-0 mt-auto pt-1">
          <span className="cs-caption text-text-default-light">{type}</span>
          <span className="cs-caption border-l border-border-medium pl-2">
            {duration}
          </span>
        </div>
      </div>
    </Link>
  );
}

